import { DesktopAlarmModal } from '../alarms/DesktopAlarmModal';
import { TradingViewChart } from '../chart/TradingViewChart';
import { ImportLoadingOverlay } from './ImportLoadingOverlay';
import { ImportResultSheet } from './ImportResultSheet';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bookmark, ChevronRight, FolderPlus, MoreHorizontal, Upload, X, type LucideIcon } from 'lucide-react';
import { forwardRef, useEffect, useMemo, useRef, useState, type ChangeEvent, type MouseEvent as ReactMouseEvent } from 'react';
import type { ChartSymbol, WatchlistFlagColor } from '../types/api';
import type { WatchlistImportSummary } from '../types/api';
import type { ApiWatchlist } from '../types/api';
import type { WatchlistQuote, WatchlistSort, WatchlistViewItem } from './AppShell.types';
import { AppLaunchScreen } from './AppLaunchScreen';
import { DesktopNavbar } from './DesktopNavbar';
import { useAppData } from './useAppData';
import './desktop-shell.css';

const watchlistSortOptions: { value: WatchlistSort; label: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'flag', label: 'Flag' },
  { value: 'symbol', label: 'Symbol A-Z' },
  { value: 'gainers', label: 'Gainers' },
  { value: 'losers', label: 'Losers' },
  { value: 'price', label: 'Price' },
];

const watchlistFlagColors: WatchlistFlagColor[] = ['red', 'orange', 'yellow', 'green', 'blue', 'purple'];

export function DesktopShell() {
  const app = useAppData();
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ fileName: string; summary: WatchlistImportSummary; } | null>(null);
  const [contextMenu, setContextMenu] = useState<DesktopContextMenuState | null>(null);
  const [dialog, setDialog] = useState<DesktopWatchlistDialog | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [watchlistMenuOpen, setWatchlistMenuOpen] = useState(false);
  const [watchlistMenuRect, setWatchlistMenuRect] = useState<DOMRect | null>(null);
  const [flagMenu, setFlagMenu] = useState<{ item: WatchlistViewItem; x: number; y: number } | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, string[]>>({});
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const headerActionsButtonRef = useRef<HTMLButtonElement | null>(null);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);
  const watchlistMenuRef = useRef<HTMLDivElement | null>(null);
  const flagMenuRef = useRef<HTMLDivElement | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 6 },
  }));
  const pinnedWatchlists = app.watchlists.filter((watchlist) => watchlist.is_pinned);
  const collapsedSectionIds = app.activeWatchlist ? new Set(collapsedSections[app.activeWatchlist.id] ?? []) : new Set<string>();
  const displayedDesktopItems = useMemo(() => filterCollapsedSections(
    sortDesktopWatchlistItems(app.activeWatchlistItems, app.watchlistSort, app.quotes),
    collapsedSectionIds,
  ), [app.activeWatchlistItems, app.watchlistSort, app.quotes, collapsedSections, app.activeWatchlist?.id]);

  function openImportPicker() {
    importInputRef.current?.click();
  }

  function openContextMenu(event: ReactMouseEvent<HTMLElement>, items: ContextMenuAction[]) {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY, items });
  }

  function openHeaderActionsMenu(event: ReactMouseEvent<HTMLElement>) {
    if (!app.canManageWatchlists) return;
    if (contextMenu?.source === 'header-actions') {
      setContextMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setContextMenu({
      x: rect.right - 190,
      y: rect.bottom + 8,
      source: 'header-actions',
      items: [
        { label: 'Create watchlist', icon: FolderPlus, action: () => setDialog({ kind: 'create-watchlist', value: '' }) },
        { label: 'Import watchlist', icon: Upload, action: openImportPicker },
      ],
    });
  }

  async function handleImportChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const result = await app.importFileToWatchlist(file);
      if (result) setImportResult({ fileName: file.name, summary: result.summary });
    } finally {
      setImporting(false);
    }
  }

  function watchlistContextMenuItems(watchlist: ApiWatchlist): ContextMenuAction[] {
	return [
	  { label: watchlist.is_pinned ? 'Unpin from favorites' : 'Pin to favorites', action: () => void app.toggleWatchlistPinned(watchlist) },
	  { label: 'Rename watchlist', action: () => setDialog({ kind: 'rename-watchlist', watchlist, value: watchlist.name }) },
	  { label: 'Delete watchlist', danger: true, action: () => setDialog({ kind: 'delete-watchlist', watchlist }) },
	]
  }

  function toggleSectionCollapsed(item: WatchlistViewItem) {
	if (!app.activeWatchlist || item.item_type !== 'section') return;
	const activeWatchlistId = app.activeWatchlist.id;
	setCollapsedSections((current) => {
	  const currentIds = current[activeWatchlistId] ?? [];
	  const nextIds = currentIds.includes(item.id) ? currentIds.filter((id) => id !== item.id) : [...currentIds, item.id];
	  return { ...current, [activeWatchlistId]: nextIds };
	});
  }

  function openFlagMenu(event: ReactMouseEvent<HTMLElement>, item: WatchlistViewItem) {
    event.preventDefault();
    event.stopPropagation();
    if (flagMenu?.item.id === item.id) {
      setFlagMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setFlagMenu({ item, x: rect.left, y: rect.bottom + 8 });
  }

  function itemContextMenuItems(item: WatchlistViewItem): ContextMenuAction[] {
	if (item.item_type === 'section') {
	  return [
	    { label: 'Rename section', action: () => setDialog({ kind: 'rename-section', item, value: item.name ?? '' }) },
	    { label: 'Create section above', action: () => setDialog({ kind: 'create-section', item, value: '' }) },
	    { label: 'Delete section', danger: true, action: () => void app.removeWatchlistItem(item) },
	  ]
	}
	return [
	  { label: 'Create section above', action: () => setDialog({ kind: 'create-section', item, value: '' }) },
	  { label: 'Remove symbol', danger: true, action: () => void app.removeWatchlistItem(item) },
	]
  }

  async function submitDialog() {
	if (!dialog) return;
	if (dialog.kind === 'create-watchlist') {
	  await app.createNewWatchlist(dialog.value)
	  setDialog(null)
	  return
	}
	if (dialog.kind === 'rename-watchlist') {
	  await app.renameWatchlistWithName(dialog.watchlist, dialog.value)
	  setDialog(null)
	  return
	}
	if (dialog.kind === 'delete-watchlist') {
	  await app.deleteWatchlistDirect(dialog.watchlist)
	  setDialog(null)
	  return
	}
	if (dialog.kind === 'rename-section') {
	  await app.renameSectionWithName(dialog.item, dialog.value)
	  setDialog(null)
	  return
	}
	if (dialog.kind === 'create-section') {
	  await app.addSectionAboveWatchlistItem(dialog.item, dialog.value)
	  setDialog(null)
	}
  }

  useEffect(() => {
    if (!contextMenu) return undefined;

    function closeContextMenu() {
      setContextMenu(null);
    }

    function handlePointerDown(event: PointerEvent) {
      if (contextMenuRef.current?.contains(event.target as Node | null)) return;
      if (headerActionsButtonRef.current?.contains(event.target as Node | null)) return;
      closeContextMenu()
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') closeContextMenu();
    }

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('blur', closeContextMenu);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('blur', closeContextMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  useEffect(() => {
    if (!sortMenuOpen) return undefined;

    function handlePointerDown(event: PointerEvent) {
      if (sortMenuRef.current?.contains(event.target as Node | null)) return;
      setSortMenuOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setSortMenuOpen(false);
    }

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [sortMenuOpen]);

  useEffect(() => {
    if (!flagMenu) return undefined;

    function handlePointerDown(event: PointerEvent) {
      if (flagMenuRef.current?.contains(event.target as Node | null)) return;
      setFlagMenu(null);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setFlagMenu(null);
    }

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [flagMenu]);

  useEffect(() => {
    if (!watchlistMenuOpen) return undefined;

    function handlePointerDown(event: PointerEvent) {
      if (watchlistMenuRef.current?.contains(event.target as Node | null)) return;
      setWatchlistMenuOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setWatchlistMenuOpen(false);
    }

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [watchlistMenuOpen]);

  useEffect(() => {
    if (!watchlistMenuOpen) return undefined;

    function updateWatchlistMenuRect() {
      setWatchlistMenuRect(watchlistMenuRef.current?.getBoundingClientRect() ?? null);
    }

    updateWatchlistMenuRect();
    window.addEventListener('resize', updateWatchlistMenuRect);
    window.addEventListener('scroll', updateWatchlistMenuRect, true);
    return () => {
      window.removeEventListener('resize', updateWatchlistMenuRect);
      window.removeEventListener('scroll', updateWatchlistMenuRect, true);
    };
  }, [watchlistMenuOpen]);

  useEffect(() => {
	function handleShortcut(event: KeyboardEvent) {
	  const key = event.key.toLowerCase();
	  if ((key !== 'w' && key !== 'b') || !event.altKey || event.metaKey || event.ctrlKey) return;
	  const target = event.target as HTMLElement | null;
	  if (target && (target.closest('input, textarea, select, [contenteditable="true"]') || target.isContentEditable)) return;
	  event.preventDefault();
	  event.stopPropagation();
	  event.stopImmediatePropagation();
	  void (key === 'w' ? app.toggleCurrentChartSymbolInWatchlist() : app.toggleCurrentChartSymbolFlag());
	}

	window.addEventListener('keydown', handleShortcut, true);
	return () => window.removeEventListener('keydown', handleShortcut, true);
  }, [app]);

  return (
    <main className="desktop-shell">
      <DesktopNavbar app={app} onOpenShortcuts={() => setShortcutsOpen(true)} />
      <section className="desktop-shell-body">
        <section className="desktop-chart-frame">
          {app.ready ? (
            <TradingViewChart
              key={app.chartSymbol}
              ref={app.chartRef}
              datafeed={app.datafeed}
              symbol={app.chartSymbol}
              interval={app.chartInterval}
              layout={app.layout}
              settingsAdapter={app.settingsAdapter}
              activeWatchlistSymbols={app.activeWatchlistSymbols}
              enableCustomIndicators={Boolean(app.user)}
              enableNineWeekInterval={app.canUseNineWeekInterval}
              onAutoSave={app.persistLayout}
              onFlush={app.flushSettings}
              onSymbolSelectorOpen={() => app.setSymbolPickerOpen(true)}
              onSymbolChange={app.setSelectedSymbol}
              onIntervalChange={app.setSelectedInterval}
              watchlistSymbolNames={app.watchlistSymbolNames}
              flaggedWatchlistSymbolNames={app.flaggedWatchlistSymbolNames}
              flaggedWatchlistSymbolColors={app.flaggedWatchlistSymbolColors}
              alarms={app.canUseAlarms ? app.chartAlarms : []}
              layoutMode="desktop"
              onDeleteAlarm={app.canUseAlarms ? (id) => void app.deleteChartAlarm(id) : undefined}
              onPlusClick={app.canUseAlarms ? (params) => void app.openAlarmForSymbolName(params.symbol, params.price) : undefined}
              draftAlarmPrice={app.canUseAlarms ? app.draftAlarmPrice : null}
              onDraftAlarmPriceChange={app.canUseAlarms ? app.setDraftAlarmPrice : undefined}
              onAddToWatchlist={app.addToWatchlistFromChart}
              onRemoveFromWatchlist={app.removeFromWatchlistFromChart}
              onToggleWatchlistShortcut={app.toggleCurrentChartSymbolInWatchlist}
              onToggleFlagShortcut={app.toggleCurrentChartSymbolFlag}
              onSetFlagColor={app.setCurrentChartSymbolFlag}
            />
          ) : (
            <AppLaunchScreen />
          )}
        </section>
        <aside className="desktop-watchlist-sidebar" aria-label="Watchlist">
          <input ref={importInputRef} type="file" accept=".txt,text/plain" hidden onChange={handleImportChange} />
          {app.canManageWatchlists && <div className="desktop-sidebar-header">
            <div className="desktop-sidebar-header-actions">
              {app.watchlists.length > 1 ? (
                <div ref={watchlistMenuRef} className="desktop-watchlist-select-menu">
                  <button
                    type="button"
                    className={app.watchlistLoading && app.watchlists.length === 0 ? 'desktop-watchlist-select-trigger desktop-watchlist-select-trigger-loading' : 'desktop-watchlist-select-trigger'}
                    aria-haspopup="menu"
                    aria-expanded={watchlistMenuOpen}
                    disabled={app.watchlistLoading && app.watchlists.length === 0}
                    onClick={() => {
                      setWatchlistMenuRect(watchlistMenuRef.current?.getBoundingClientRect() ?? null);
                      setWatchlistMenuOpen((open) => !open);
                    }}
                  >
                    {app.watchlistLoading && app.watchlists.length === 0 ? <span className="desktop-skeleton desktop-skeleton-watchlist-name" aria-hidden="true" /> : <span>{app.activeWatchlist?.name ?? 'Watchlist'}</span>}
                  </button>
                  {watchlistMenuOpen ? (
                    <div
                      className="desktop-watchlist-select-popover"
                      role="menu"
                      style={watchlistMenuRect ? {
                        left: watchlistMenuRect.left,
                        top: watchlistMenuRect.bottom + 8,
                        width: watchlistMenuRect.width,
                      } : undefined}
                    >
                      {app.watchlists.map((watchlist) => (
                        <button
                          key={watchlist.id}
                          type="button"
                          role="menuitemradio"
                          aria-checked={watchlist.id === app.activeWatchlist?.id}
                          title={watchlist.name}
                          onClick={() => {
                            app.setActiveWatchlistId(watchlist.id);
                            setWatchlistMenuOpen(false);
                          }}
                        >
                          <strong>{watchlist.name}</strong>
                        </button>
                      ))}
                      {app.canManageWatchlists && app.activeWatchlist ? (
                        <>
                          <div className="desktop-watchlist-select-separator" />
                          {watchlistContextMenuItems(app.activeWatchlist).map((item) => (
                            <button
                              key={item.label}
                              type="button"
                              role="menuitem"
                              className={item.danger ? 'danger' : ''}
                              onClick={() => {
                                setWatchlistMenuOpen(false);
                                void item.action();
                              }}
                            >
                              <strong>{item.label}</strong>
                            </button>
                          ))}
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div
                  className="desktop-watchlist-single-name"
                  onContextMenu={(event) => {
                    event.preventDefault();
                  }}
                >
                  {app.watchlistLoading && app.watchlists.length === 0 ? <span className="desktop-skeleton desktop-skeleton-watchlist-name" aria-hidden="true" /> : app.activeWatchlist?.name ?? 'Watchlist'}
                </div>
              )}
              {app.canManageWatchlists ? (
                <button
                  ref={headerActionsButtonRef}
                  type="button"
                  className="desktop-sidebar-icon-button"
                  aria-label="Watchlist actions"
                  title="Watchlist actions"
                  onClick={openHeaderActionsMenu}
                >
                  <MoreHorizontal size={19} strokeWidth={2.1} />
                </button>
              ) : null}
            </div>
          </div>}
          {app.canManageWatchlists && pinnedWatchlists.length > 0 ? (
            <div className="desktop-watchlist-favorites" aria-label="Favorite watchlists">
              {pinnedWatchlists.map((watchlist) => (
                <button
                  key={watchlist.id}
                  type="button"
                  className={watchlist.id === app.activeWatchlist?.id ? 'desktop-watchlist-favorite-chip active' : 'desktop-watchlist-favorite-chip'}
                  title={watchlist.name}
                  aria-label={`Open ${watchlist.name}`}
                  onClick={() => app.setActiveWatchlistId(watchlist.id)}
                  onContextMenu={(event) => openContextMenu(event, watchlistContextMenuItems(watchlist))}
                >
                  {favoriteWatchlistLetter(watchlist.name)}
                </button>
              ))}
            </div>
          ) : null}
          {app.watchlistError && <div className="desktop-sidebar-error">{app.watchlistError}</div>}
          <div className="desktop-watchlist-table-head">
            <button
              type="button"
              className={app.watchlistSort === 'flag' ? 'desktop-watchlist-flag-sort active' : 'desktop-watchlist-flag-sort'}
              title={app.watchlistSort === 'flag' ? 'Return to manual sort' : 'Sort by flag'}
              aria-label={app.watchlistSort === 'flag' ? 'Return to manual sort' : 'Sort by flag'}
              aria-pressed={app.watchlistSort === 'flag'}
              onClick={() => app.setWatchlistSort(app.watchlistSort === 'flag' ? 'manual' : 'flag')}
            >
              <Bookmark size={12} strokeWidth={2.2} fill="currentColor" aria-hidden="true" />
              <span>Symbol</span>
            </button>
            <div ref={sortMenuRef} className="desktop-watchlist-sort-menu">
              <button type="button" className="desktop-watchlist-sort-trigger" aria-haspopup="menu" aria-expanded={sortMenuOpen} onClick={() => setSortMenuOpen((open) => !open)}>
                <span>Sort by</span>
              </button>
              {sortMenuOpen ? (
                <div className="desktop-watchlist-sort-popover" role="menu">
                  {watchlistSortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="menuitemradio"
                      aria-checked={app.watchlistSort === option.value}
                      onClick={() => {
                        app.setWatchlistSort(option.value);
                        setSortMenuOpen(false);
                      }}
                    >
                      <span>{app.watchlistSort === option.value ? '✓' : ''}</span>
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(event: DragStartEvent) => app.startReorder(String(event.active.id), app.activeWatchlistItems)}
            onDragOver={(event: DragOverEvent) => {
              if (!event.over) return;
              app.previewReorder(String(event.active.id), String(event.over.id));
            }}
            onDragEnd={(event: DragEndEvent) => {
              if (!event.over) {
                app.cancelReorder();
                return;
              }
              void app.finishReorder();
            }}
            onDragCancel={app.cancelReorder}
          >
            <SortableContext items={displayedDesktopItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
              <div className="desktop-watchlist-list">
                {app.watchlistLoading && app.activeWatchlistItems.length === 0 ? <DesktopWatchlistSkeletonRows /> : app.activeWatchlistItems.length > 0 ? displayedDesktopItems.map((item) => (
                  <DesktopWatchlistItem
                    key={item.id}
                    item={item}
                    collapsed={item.item_type === 'section' && collapsedSectionIds.has(item.id)}
                    selectedSymbol={app.chartSymbol}
                    quote={item.symbolData && !item.isInactive ? app.quotes[item.symbolData.name] : undefined}
                    dragging={app.draggingItemId === item.id}
                    dragOver={app.dragOverItemId === item.id}
                    onSelect={(symbol) => app.setSelectedSymbol(symbol.name)}
                    onToggleSection={toggleSectionCollapsed}
                    onOpenFlagMenu={openFlagMenu}
                    onContextMenu={(event, currentItem) => openContextMenu(event, itemContextMenuItems(currentItem))}
                  />
                )) : (
                  <div className="desktop-sidebar-empty">No symbols yet.</div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        </aside>
      </section>
      {app.canUseAlarms ? (
        <DesktopAlarmModal
          open={Boolean(app.alarmSymbol)}
          symbol={app.alarmSymbol}
          initialPrice={app.alarmInitialPrice ?? (app.alarmSymbol ? app.quotes[app.alarmSymbol.name]?.price : undefined)}
          onDraftPriceChange={app.setDraftAlarmPrice}
          onSaved={app.addSavedAlarm}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) app.closeAlarmSetup();
          }}
        />
      ) : null}
      <ImportLoadingOverlay open={importing} />
      <ImportResultSheet
        open={Boolean(importResult)}
        fileName={importResult?.fileName ?? ''}
        summary={importResult?.summary ?? null}
        desktop
        onOpenChange={(open) => {
          if (!open) setImportResult(null);
        }}
      />
      <DesktopContextMenu ref={contextMenuRef} menu={contextMenu} onClose={() => setContextMenu(null)} />
      <DesktopFlagMenu ref={flagMenuRef} menu={flagMenu} onSelect={(item, flagColor) => {
        setFlagMenu(null);
        void app.setWatchlistItemFlagColor(item, flagColor);
      }} />
      <DesktopWatchlistDialogModal dialog={dialog} onOpenChange={(open) => {
		if (!open) setDialog(null)
	  }} onValueChange={(value) => setDialog((current) => current && 'value' in current ? { ...current, value } : current)} onSubmit={() => void submitDialog()} />
      <DesktopShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </main>
  );
}

function DesktopWatchlistSkeletonRows() {
  return Array.from({ length: 6 }, (_, index) => (
    <div key={index} className="desktop-watchlist-row desktop-watchlist-skeleton-row" aria-hidden="true">
      <span className="desktop-watchlist-symbol">
        <span className="desktop-skeleton desktop-skeleton-symbol" />
        <span className="desktop-skeleton desktop-skeleton-description" />
      </span>
      <span className="desktop-watchlist-quote desktop-watchlist-quote-loading">
        <span className="desktop-skeleton desktop-skeleton-price" />
        <span className="desktop-skeleton desktop-skeleton-change" />
      </span>
    </div>
  ));
}

function DesktopWatchlistItem({ item, collapsed, selectedSymbol, quote, dragging, dragOver, onSelect, onToggleSection, onOpenFlagMenu, onContextMenu }: {
  item: WatchlistViewItem;
  collapsed: boolean;
  selectedSymbol: string;
  quote: WatchlistQuote | undefined;
  dragging: boolean;
  dragOver: boolean;
  onSelect(symbol: ChartSymbol): void;
  onToggleSection(item: WatchlistViewItem): void;
  onOpenFlagMenu(event: ReactMouseEvent<HTMLElement>, item: WatchlistViewItem): void;
  onContextMenu(event: ReactMouseEvent<HTMLElement>, item: WatchlistViewItem): void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  if (item.item_type !== 'symbol' || !item.symbolData) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={[
          'desktop-watchlist-section',
          collapsed ? 'collapsed' : '',
          dragging || isDragging ? 'dragging' : '',
          dragOver ? 'drag-over' : '',
        ].filter(Boolean).join(' ')}
        {...attributes}
        {...listeners}
        role="button"
        tabIndex={0}
        onClick={() => onToggleSection(item)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggleSection(item);
          }
        }}
        onContextMenu={(event) => onContextMenu(event, item)}
      >
        <ChevronRight className="desktop-watchlist-section-chevron" size={14} strokeWidth={2.2} aria-hidden="true" />
        <span>{item.name}</span>
      </div>
    );
  }

  const symbol = item.symbolData;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      className={[
        'desktop-watchlist-row',
        'desktop-watchlist-data-row',
        symbol.name === selectedSymbol ? 'active' : '',
        dragging || isDragging ? 'dragging' : '',
        dragOver ? 'drag-over' : '',
      ].filter(Boolean).join(' ')}
      onClick={() => onSelect(symbol)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(symbol);
        }
      }}
      onContextMenu={(event) => onContextMenu(event, item)}
    >
      <button
        type="button"
        className={item.flag_color ? `desktop-watchlist-flag-button flag-${item.flag_color}` : 'desktop-watchlist-flag-button'}
        aria-label={item.flag_color ? `Change ${symbol.name} flag` : `Flag ${symbol.name}`}
        title={item.flag_color ? `Flag: ${item.flag_color}` : 'Add flag'}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => onOpenFlagMenu(event, item)}
      >
        <Bookmark size={13} strokeWidth={2.2} fill={item.flag_color ? 'currentColor' : 'none'} aria-hidden="true" />
      </button>
      <span className={item.isInactive ? 'desktop-watchlist-symbol desktop-watchlist-symbol-inactive' : 'desktop-watchlist-symbol'}>
        <strong>{symbol.name}</strong>
        <span>{symbol.description}</span>
      </span>
      <DesktopQuoteBlock symbol={symbol} quote={quote} inactive={Boolean(item.isInactive)} />
    </div>
  );
}

type ContextMenuAction = {
  label: string;
  icon?: LucideIcon;
  danger?: boolean;
  action(): void | Promise<void>;
};

type DesktopContextMenuState = {
  x: number;
  y: number;
  source?: 'header-actions';
  items: ContextMenuAction[];
};

type DesktopWatchlistDialog =
	| { kind: 'create-watchlist'; value: string }
	| { kind: 'rename-watchlist'; watchlist: ApiWatchlist; value: string }
	| { kind: 'delete-watchlist'; watchlist: ApiWatchlist }
	| { kind: 'rename-section'; item: WatchlistViewItem; value: string }
	| { kind: 'create-section'; item: WatchlistViewItem; value: string }

const DesktopContextMenu = forwardRef<HTMLDivElement, { menu: DesktopContextMenuState | null; onClose(): void }>(function DesktopContextMenu({ menu, onClose }, ref) {
  if (!menu) return null;

  return (
    <div ref={ref} className="desktop-context-menu" style={{ left: menu.x, top: menu.y }} role="menu">
      {menu.items.map((item) => (
        <button
          key={item.label}
          type="button"
          className={item.danger ? 'desktop-context-menu-item desktop-context-menu-item-danger' : 'desktop-context-menu-item'}
          onClick={() => {
            onClose();
            void item.action();
          }}
        >
          {item.icon ? <item.icon size={15} strokeWidth={2} aria-hidden="true" /> : null}
          {item.label}
        </button>
      ))}
    </div>
  );
})

const DesktopFlagMenu = forwardRef<HTMLDivElement, {
  menu: { item: WatchlistViewItem; x: number; y: number } | null;
  onSelect(item: WatchlistViewItem, flagColor: WatchlistFlagColor | null): void;
}>(function DesktopFlagMenu({ menu, onSelect }, ref) {
  if (!menu) return null;

  return (
    <div ref={ref} className="desktop-flag-menu" style={{ left: menu.x, top: menu.y }} role="menu" aria-label="Flag color">
      {watchlistFlagColors.map((color) => (
        <button
          key={color}
          type="button"
          className={`desktop-flag-menu-color flag-${color}`}
          aria-label={`Set ${color} flag`}
          aria-checked={menu.item.flag_color === color}
          role="menuitemradio"
          onClick={() => onSelect(menu.item, color)}
        >
          <Bookmark size={15} strokeWidth={2.1} fill="currentColor" aria-hidden="true" />
        </button>
      ))}
      <button type="button" className="desktop-flag-menu-clear" role="menuitem" onClick={() => onSelect(menu.item, null)}>Clear</button>
    </div>
  );
})

function DesktopWatchlistDialogModal({ dialog, onOpenChange, onValueChange, onSubmit }: {
	dialog: DesktopWatchlistDialog | null;
	onOpenChange(open: boolean): void;
	onValueChange(value: string): void;
	onSubmit(): void;
}) {
	if (!dialog) return null;
	const destructive = dialog.kind === 'delete-watchlist'
	const confirmLabel = dialog.kind === 'create-watchlist' ? 'Create' : dialog.kind === 'create-section' ? 'Create' : dialog.kind === 'delete-watchlist' ? 'Delete' : 'Save'
	const title = dialog.kind === 'create-watchlist'
	  ? 'Create watchlist'
	  : dialog.kind === 'rename-watchlist'
	    ? 'Rename watchlist'
	    : dialog.kind === 'delete-watchlist'
	      ? 'Delete watchlist'
	      : dialog.kind === 'rename-section'
	        ? 'Rename section'
	        : 'Create section above'
	const value = 'value' in dialog ? dialog.value : ''
	const needsInput = dialog.kind !== 'delete-watchlist'
	const subtitle = dialog.kind === 'delete-watchlist' ? `Delete ${dialog.watchlist.name}?` : undefined

	return (
	  <div className="desktop-alarm-backdrop" onMouseDown={() => onOpenChange(false)}>
	    <section className="desktop-alarm-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
	      <header className="desktop-alarm-header">
	        <div>
	          <strong>{title}</strong>
	          {subtitle ? <span>{subtitle}</span> : null}
	        </div>
	        <button type="button" className="desktop-alarm-icon-button" aria-label="Close dialog" onClick={() => onOpenChange(false)}>
	          <X size={18} strokeWidth={2.2} />
	        </button>
	      </header>
	      {needsInput ? (
	        <label className="desktop-alarm-price-field">
	          <span>Name</span>
	          <input value={value} onChange={(event) => onValueChange(event.target.value)} onKeyDown={(event) => {
	            if (event.key === 'Enter') onSubmit()
	            if (event.key === 'Escape') onOpenChange(false)
	          }} />
	        </label>
	      ) : null}
	      <footer className="desktop-alarm-footer">
	        <button type="button" onClick={() => onOpenChange(false)}>Cancel</button>
	        <button type="button" className={destructive ? '' : 'primary'} onClick={onSubmit} disabled={needsInput && !value.trim()}>{confirmLabel}</button>
	      </footer>
	    </section>
	  </div>
	)
}

function DesktopShortcutsModal({ open, onOpenChange }: { open: boolean; onOpenChange(open: boolean): void }) {
	if (!open) return null;

	return (
	  <div className="desktop-alarm-backdrop" onMouseDown={() => onOpenChange(false)}>
	    <section className="desktop-import-modal desktop-shortcuts-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
	      <header className="desktop-alarm-header">
	        <div>
	          <strong>Shortcuts</strong>
	          <span>Keyboard shortcuts for watchlist actions</span>
	        </div>
	        <button type="button" className="desktop-alarm-icon-button" aria-label="Close shortcuts" onClick={() => onOpenChange(false)}>
	          <X size={18} strokeWidth={2.2} />
	        </button>
	      </header>
	      <div className="desktop-shortcuts-table" role="table" aria-label="Shortcut table">
	        <div className="desktop-shortcuts-row desktop-shortcuts-row-head" role="row">
	          <span role="columnheader">Shortcut</span>
	          <span role="columnheader">Action</span>
	        </div>
	        <div className="desktop-shortcuts-row" role="row">
	          <span role="cell"><kbd>Option</kbd><kbd>W</kbd></span>
	          <span role="cell">Add or remove the current chart symbol from the active watchlist</span>
	        </div>
	        <div className="desktop-shortcuts-row" role="row">
	          <span role="cell"><kbd>Option</kbd><kbd>B</kbd></span>
	          <span role="cell">Flag or unflag the current chart symbol with the last used flag color</span>
	        </div>
	      </div>
	      <footer className="desktop-alarm-footer">
	        <button type="button" className="primary" onClick={() => onOpenChange(false)}>Done</button>
	      </footer>
	    </section>
	  </div>
	)
}

function DesktopQuoteBlock({ symbol, quote, inactive }: { symbol: ChartSymbol; quote: WatchlistQuote | undefined; inactive: boolean }) {
  if (inactive) {
    return <span className="desktop-watchlist-quote desktop-watchlist-quote-inactive">Inactive</span>;
  }
  if (!quote) {
    return (
      <span className="desktop-watchlist-quote desktop-watchlist-quote-loading" aria-label="Loading quote">
        <span className="desktop-skeleton desktop-skeleton-price" />
        <span className="desktop-skeleton desktop-skeleton-change" />
      </span>
    );
  }

  const direction = quote.change >= 0 ? 'positive' : 'negative';
  const sign = quote.change > 0 ? '+' : '';
  const decimals = decimalsFromPricescale(symbol.pricescale);
  const changeText = `${sign}${formatChange(quote.change, decimals)} ${formatPercent(quote.percent)}`;

  return (
    <span className={`desktop-watchlist-quote ${direction}`}>
      <strong>{formatPrice(quote.price, decimals)}</strong>
      <span>{changeText}</span>
    </span>
  );
}

function formatPrice(value: number, decimals?: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimals ?? (value >= 100 ? 2 : 4),
    minimumFractionDigits: decimals ?? (value >= 1 ? 2 : 0),
  }).format(value);
}

function formatChange(value: number, decimals?: number): string {
  return Math.abs(value).toFixed(decimals ?? (Math.abs(value) >= 1 ? 2 : 4));
}

function formatPercent(value: number): string {
  if (value === 0) return '0%';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function decimalsFromPricescale(pricescale: number): number {
  if (!Number.isFinite(pricescale) || pricescale <= 1) return 0;
  return Math.min(8, Math.max(0, Math.round(Math.log10(pricescale))));
}

function favoriteWatchlistLetter(name: string): string {
  return (name.trim().charAt(0) || 'W').toUpperCase();
}

function filterCollapsedSections(items: WatchlistViewItem[], collapsedSectionIds: Set<string>): WatchlistViewItem[] {
  if (collapsedSectionIds.size === 0) return items;

  const visible: WatchlistViewItem[] = [];
  let hidden = false;
  for (const item of items) {
    if (item.item_type === 'section') {
      hidden = collapsedSectionIds.has(item.id);
      visible.push(item);
      continue;
    }
    if (!hidden) visible.push(item);
  }
  return visible;
}

function sortDesktopWatchlistItems(items: WatchlistViewItem[], sort: WatchlistSort, quotes: Record<string, WatchlistQuote | undefined>): WatchlistViewItem[] {
  if (sort === 'manual') return items;

  const activeSort = sort;
  const sortedItems: WatchlistViewItem[] = [];
  let sectionSymbols: WatchlistViewItem[] = [];

  function flushSectionSymbols() {
    if (sectionSymbols.length === 0) return;
    sortedItems.push(...sectionSymbols.sort((a, b) => compareDesktopSymbols(a, b, activeSort, quotes)));
    sectionSymbols = [];
  }

  for (const item of items) {
    if (item.item_type === 'section') {
      flushSectionSymbols();
      sortedItems.push(item);
    } else if (item.symbolData) {
      sectionSymbols.push(item);
    } else {
      flushSectionSymbols();
      sortedItems.push(item);
    }
  }

  flushSectionSymbols();
  return sortedItems;
}

function compareDesktopSymbols(a: WatchlistViewItem, b: WatchlistViewItem, sort: Exclude<WatchlistSort, 'manual'>, quotes: Record<string, WatchlistQuote | undefined>): number {
  const symbolA = a.symbolData;
  const symbolB = b.symbolData;
  if (!symbolA || !symbolB) return 0;
  if (sort === 'flag') return compareFlaggedItems(a, b) || symbolA.name.localeCompare(symbolB.name);
  if (sort === 'symbol') return symbolA.name.localeCompare(symbolB.name);

  const quoteA = quotes[symbolA.name];
  const quoteB = quotes[symbolB.name];
  if (!quoteA && !quoteB) return symbolA.name.localeCompare(symbolB.name);
  if (!quoteA) return 1;
  if (!quoteB) return -1;

  if (sort === 'gainers') return quoteB.percent - quoteA.percent || symbolA.name.localeCompare(symbolB.name);
  if (sort === 'losers') return quoteA.percent - quoteB.percent || symbolA.name.localeCompare(symbolB.name);
  return quoteB.price - quoteA.price || symbolA.name.localeCompare(symbolB.name);
}

function compareFlaggedItems(a: WatchlistViewItem, b: WatchlistViewItem): number {
  const rankA = flagColorRank(a.flag_color);
  const rankB = flagColorRank(b.flag_color);
  return rankA - rankB;
}

function flagColorRank(color: WatchlistFlagColor | null | undefined): number {
  if (!color) return 999;
  const index = watchlistFlagColors.indexOf(color);
  return index >= 0 ? index : 999;
}
